import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  BookingAuthSession,
  BookingForgotPasswordRequest,
  BookingForgotPasswordVerifyRequest,
  BookingForgotPasswordVerifyResponse,
  BookingLoginRequest,
  BookingProfileResponse,
  BookingProfileUpdateRequest,
  BookingRegisterRequest,
  BookingResetPasswordRequest,
  BookingTokenResponse,
  BookingValidateEmailCodeRequest,
} from '../models/booking/booking-auth.model';
import { AppService } from './app.service';

type ParsedBookingAuthResponse =
  | BookingAuthSession
  | BookingTokenResponse
  | null;

const bookingSessionStorageKey = 'icid.bookingAuth.session';
const bookingTokenStorageKey = 'icid.bookingAuth.token';
const bookingAuthCookieName = 'icid.bookingAuth.token';
const bookingAuthCookieMaxAgeSeconds = 60 * 60 * 24 * 7;
const bookingAuthApiUrl = '/client-customer';
const authenticationApiUrl = '/authentication';

@Injectable({
  providedIn: 'root',
})
export class BookingAuthService {
  private readonly appService = inject(AppService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly sessionState = signal<BookingAuthSession | null>(null);
  private readonly tokenState = signal<string | null>(null);
  private readonly isLoadingProfileState = signal(false);

  readonly session = this.sessionState.asReadonly();
  readonly isLoadingProfile = this.isLoadingProfileState.asReadonly();
  readonly user = computed(() => this.sessionState()?.user ?? null);

  readonly isLoggedIn = computed(() => Boolean(this.getCurrentToken()));

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    const storedAuth =
      window.localStorage.getItem(bookingSessionStorageKey) ??
      window.localStorage.getItem(bookingTokenStorageKey) ??
      this.getTokenCookie();

    if (!storedAuth) {
      return;
    }

    this.restoreSessionFromStorage(storedAuth);
  }

  getToken(): string | null {
    return this.getCurrentToken();
  }

  async login(request: BookingLoginRequest): Promise<void> {
    const response = await this.requestAuthResponse(
      `${authenticationApiUrl}/login`,
      request,
    );

    this.applyAuthResponse(response);
  }

  async register(request: BookingRegisterRequest): Promise<void> {
    const response = await this.requestAuthResponse(
      `${bookingAuthApiUrl}/register`,
      request,
    );

    this.applyAuthResponseIfPresent(response);
  }

  async refreshProfile(): Promise<BookingProfileResponse | null> {
    const token = this.getCurrentToken();

    if (!token) {
      return null;
    }

    this.isLoadingProfileState.set(true);

    try {
      const profile = await this.getProfile(token);
      this.setSession({ token, ...profile });
      return profile;
    } catch (error) {
      this.clearSession();
      throw error;
    } finally {
      this.isLoadingProfileState.set(false);
    }
  }

  async updateProfile(
    request: BookingProfileUpdateRequest,
  ): Promise<BookingProfileResponse> {
    const currentSession = this.requireSession();
    const profile = await this.appService.request<unknown>(
      'PUT',
      `${bookingAuthApiUrl}/profile`,
      {
        body: request,
        headers: this.createAuthHeaders(currentSession.token),
      },
    );
    const normalizedProfile = this.normalizeBookingProfileResponse(profile);

    this.setSession({ ...currentSession, ...normalizedProfile });
    return normalizedProfile;
  }

  async resendValidationEmail(): Promise<void> {
    const token = this.requireToken();

    await this.appService.request<unknown>(
      'POST',
      `${bookingAuthApiUrl}/resend-validation-email`,
      { headers: this.createAuthHeaders(token) },
    );
  }

  async validateEmailCode(
    request: BookingValidateEmailCodeRequest,
  ): Promise<void> {
    const token = this.getCurrentToken();
    const headers = token ? this.createAuthHeaders(token) : undefined;

    const response = await this.requestAuthResponse(
      `${bookingAuthApiUrl}/validate-email`,
      {
        Email: request.email,
        EmailValidationCode: request.EmailValidationCode,
      },
      headers,
    );

    this.applyAuthResponseIfPresent(response);
  }

  async forgotPassword(request: BookingForgotPasswordRequest): Promise<boolean> {
    const response = await this.appService.request<unknown>(
      'POST',
      `${authenticationApiUrl}/forgot-password`,
      { body: request },
    );
    const responseRecord = this.asRecord(
      this.unwrapAuthResponse(this.parseUnknownResponse(response)),
    );

    return this.getBooleanValue(
      responseRecord,
      ['passwordResetEmailSent', 'PasswordResetEmailSent'],
      true,
    );
  }

  async verifyForgotPasswordCode(
    request: BookingForgotPasswordVerifyRequest,
  ): Promise<BookingForgotPasswordVerifyResponse> {
    const response = await this.appService.request<unknown>(
      'POST',
      `${authenticationApiUrl}/forgot-password/verify`,
      { body: request },
    );

    return this.normalizeForgotPasswordVerifyResponse(response);
  }

  async resetPassword(request: BookingResetPasswordRequest): Promise<boolean> {
    const response = await this.appService.request<unknown>(
      'POST',
      `${authenticationApiUrl}/reset-password`,
      { body: request },
    );
    const responseRecord = this.asRecord(
      this.unwrapAuthResponse(this.parseUnknownResponse(response)),
    );

    return this.getBooleanValue(
      responseRecord,
      ['passwordReset', 'PasswordReset'],
      true,
    );
  }

  logout(): void {
    this.clearSession();
  }

  private requireSession(): BookingAuthSession {
    const currentSession = this.sessionState();

    if (!currentSession) {
      throw new Error('A booking auth session is required.');
    }

    return currentSession;
  }

  private requireToken(): string {
    const token = this.getCurrentToken();

    if (!token) {
      throw new Error('A booking auth token is required.');
    }

    return token;
  }

  private getCurrentToken(): string | null {
    return this.sessionState()?.token ?? this.tokenState();
  }

  private setSession(session: BookingAuthSession): void {
    this.sessionState.set(session);
    this.tokenState.set(session.token);

    if (this.isBrowser) {
      this.persistToken(session.token);
      window.localStorage.setItem(
        bookingSessionStorageKey,
        JSON.stringify(session),
      );
    }
  }

  private setToken(token: string): void {
    this.tokenState.set(token);

    if (this.isBrowser) {
      this.persistToken(token);
    }
  }

  private clearSession(): void {
    this.sessionState.set(null);
    this.tokenState.set(null);

    if (this.isBrowser) {
      window.localStorage.removeItem(bookingSessionStorageKey);
      window.localStorage.removeItem(bookingTokenStorageKey);
      this.clearTokenCookie();
    }
  }

  private createAuthHeaders(token: string): Record<string, string> {
    return { Authorization: `Bearer ${token}` };
  }

  private persistToken(token: string): void {
    if (!this.isBrowser) {
      return;
    }

    window.localStorage.setItem(bookingTokenStorageKey, token);
    document.cookie = `${bookingAuthCookieName}=${encodeURIComponent(token)};max-age=${bookingAuthCookieMaxAgeSeconds};path=/;SameSite=Lax${this.getSecureCookieSuffix()}`;
  }

  private getTokenCookie(): string | null {
    const cookie = document.cookie
      .split(';')
      .map((value) => value.trim())
      .find((value) => value.startsWith(`${bookingAuthCookieName}=`));

    if (!cookie) {
      return null;
    }

    return decodeURIComponent(
      cookie.substring(bookingAuthCookieName.length + 1),
    );
  }

  private clearTokenCookie(): void {
    document.cookie = `${bookingAuthCookieName}=;max-age=0;path=/;SameSite=Lax${this.getSecureCookieSuffix()}`;
  }

  private getSecureCookieSuffix(): string {
    return window.location.protocol === 'https:' ? ';Secure' : '';
  }

  private restoreSessionFromStorage(storedSession: string): void {
    try {
      const parsedSession = this.parseStoredAuthResponse(storedSession);
      const token = this.extractToken(parsedSession);

      if (!token) {
        throw new Error('Stored authentication did not include a token.');
      }

      const profile = this.tryNormalizeBookingProfileResponse(parsedSession);

      if (profile) {
        this.setSession({ token, ...profile });
        return;
      }

      this.setToken(token);
    } catch (_error) {
      this.clearSession();
    }
  }

  private parseStoredAuthResponse(
    storedSession: string,
  ): ParsedBookingAuthResponse {
    try {
      return JSON.parse(storedSession) as
        | BookingAuthSession
        | BookingTokenResponse;
    } catch (_error) {
      return storedSession;
    }
  }

  private async requestAuthResponse(
    url: string,
    body:
      | BookingLoginRequest
      | BookingRegisterRequest
      | { Email: string; EmailValidationCode: string },
    headers?: Record<string, string>,
  ): Promise<ParsedBookingAuthResponse> {
    const response = await this.appService.request<string>('POST', url, {
      body,
      headers: { Accept: 'application/json, text/plain', ...headers },
      responseType: 'text',
    });

    return this.parseAuthResponse(response);
  }

  private parseAuthResponse(
    response: string | null,
  ): ParsedBookingAuthResponse {
    const trimmedResponse = response?.trim() ?? '';

    if (!trimmedResponse) {
      return null;
    }

    try {
      return JSON.parse(trimmedResponse) as
        | BookingAuthSession
        | BookingTokenResponse;
    } catch (_error) {
      return trimmedResponse;
    }
  }

  private applyAuthResponse(response: ParsedBookingAuthResponse): void {
    if (this.applyAuthResponseIfPresent(response)) {
      return;
    }

    throw new Error('The authentication response did not include a token.');
  }

  private applyAuthResponseIfPresent(
    response: ParsedBookingAuthResponse,
  ): boolean {
    const token = this.extractToken(response);

    if (!token) {
      return false;
    }

    const profile = this.tryNormalizeBookingProfileResponse(response);

    if (profile) {
      this.setSession({ token, ...profile });
      return true;
    }

    this.sessionState.set(null);
    this.setToken(token);

    if (this.isBrowser) {
      window.localStorage.removeItem(bookingSessionStorageKey);
    }

    return true;
  }

  private async getProfile(token: string): Promise<BookingProfileResponse> {
    const profile = await this.appService.request<unknown>(
      'GET',
      `${bookingAuthApiUrl}/profile`,
      { headers: this.createAuthHeaders(token) },
    );

    return this.normalizeBookingProfileResponse(profile);
  }

  private normalizeBookingProfileResponse(
    rawProfile: unknown,
  ): BookingProfileResponse {
    const profile = this.asRecord(this.unwrapAuthResponse(rawProfile));
    const rawUser =
      profile['user'] ??
      profile['User'] ??
      profile['clientCustomer'] ??
      profile['ClientCustomer'] ??
      profile['customer'] ??
      profile['Customer'] ??
      profile;
    const user = this.asRecord(rawUser);

    return {
      user: {
        id: this.getStringValue(user, [
          'id',
          'Id',
          'customerId',
          'CustomerId',
          'clientCustomerId',
          'ClientCustomerId',
        ]),
        firstName: this.getStringValue(user, ['firstName', 'FirstName']),
        lastName: this.getStringValue(user, ['lastName', 'LastName']),
        email: this.getStringValue(user, ['email', 'Email']),
        mobilePhoneNumber: this.getStringValue(user, [
          'mobilePhoneNumber',
          'MobilePhoneNumber',
          'mobilePhone',
          'MobilePhone',
          'phoneNumber',
          'PhoneNumber',
        ]),
        emailValidated: this.getBooleanValue(user, [
          'emailValidated',
          'EmailValidated',
          'isEmailValidated',
          'IsEmailValidated',
          'emailVerified',
          'EmailVerified',
        ]),
      },
    };
  }

  private tryNormalizeBookingProfileResponse(
    rawProfile: unknown,
  ): BookingProfileResponse | null {
    const profile = this.normalizeBookingProfileResponse(rawProfile);

    return profile.user.id || profile.user.email ? profile : null;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return typeof value === 'object' && value !== null
      ? (value as Record<string, unknown>)
      : {};
  }

  private getStringValue(
    record: Record<string, unknown>,
    keys: string[],
  ): string {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'string') {
        return value;
      }

      if (typeof value === 'number') {
        return value.toString();
      }
    }

    return '';
  }

  private getBooleanValue(
    record: Record<string, unknown>,
    keys: string[],
    fallback = false,
  ): boolean {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'string') {
        return value.trim().toLowerCase() === 'true';
      }

      if (typeof value === 'number') {
        return value === 1;
      }
    }

    return fallback;
  }

  private getNumberValue(
    record: Record<string, unknown>,
    keys: string[],
  ): number | null {
    for (const key of keys) {
      const value = record[key];

      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string' && value.trim()) {
        const numberValue = Number(value);

        if (Number.isFinite(numberValue)) {
          return numberValue;
        }
      }
    }

    return null;
  }

  private normalizeForgotPasswordVerifyResponse(
    response: unknown,
  ): BookingForgotPasswordVerifyResponse {
    const responseRecord = this.asRecord(
      this.unwrapAuthResponse(this.parseUnknownResponse(response)),
    );
    const isVerified = this.getBooleanValue(responseRecord, [
      'isVerified',
      'IsVerified',
    ]);
    const resetRequestId = this.getNumberValue(responseRecord, [
      'resetRequestId',
      'ResetRequestId',
    ]);
    const resetToken = this.getStringValue(responseRecord, [
      'resetToken',
      'ResetToken',
    ]);
    const resetTokenExpiresAtUtc = this.getStringValue(responseRecord, [
      'resetTokenExpiresAtUtc',
      'ResetTokenExpiresAtUtc',
    ]);

    if (
      !isVerified ||
      resetRequestId === null ||
      !resetToken ||
      !resetTokenExpiresAtUtc
    ) {
      throw new Error('The password reset code could not be verified.');
    }

    return {
      isVerified,
      resetRequestId,
      resetToken,
      resetTokenExpiresAtUtc,
    };
  }

  private parseUnknownResponse(response: unknown): unknown {
    let parsedResponse = response;

    for (let index = 0; index < 2; index += 1) {
      if (typeof parsedResponse !== 'string') {
        break;
      }

      const trimmedResponse = parsedResponse.trim();

      if (!trimmedResponse) {
        return {};
      }

      try {
        parsedResponse = JSON.parse(trimmedResponse) as unknown;
      } catch (_error) {
        return trimmedResponse;
      }
    }

    return parsedResponse;
  }

  private extractToken(
    response: ParsedBookingAuthResponse,
    depth = 0,
  ): string | null {
    if (depth > 4 || response === null) {
      return null;
    }

    if (typeof response === 'string') {
      const token = response.trim();

      if (token.length > 0) {
        return token;
      }
    }

    if (typeof response === 'object' && response !== null) {
      const tokenResponse = response as Exclude<BookingTokenResponse, string>;
      const token =
        tokenResponse.token ??
        tokenResponse.Token ??
        tokenResponse.jwtToken ??
        tokenResponse.JwtToken ??
        tokenResponse.accessToken ??
        tokenResponse.AccessToken ??
        tokenResponse.bearerToken ??
        tokenResponse.BearerToken;

      if (token) {
        return token;
      }

      const unwrappedResponse = this.unwrapAuthResponse(response);

      if (unwrappedResponse !== response) {
        return this.extractToken(
          unwrappedResponse as ParsedBookingAuthResponse,
          depth + 1,
        );
      }
    }

    return null;
  }

  private unwrapAuthResponse(response: unknown): unknown {
    const record = this.asRecord(response);

    return (
      record['data'] ??
      record['Data'] ??
      record['result'] ??
      record['Result'] ??
      record['value'] ??
      record['Value'] ??
      response
    );
  }
}
